import React, { useState, useEffect } from 'react';
import './TopicManager.css';
import QuickPost from './QuickPost';

function TopicManager({ user }) {
  const [topics, setTopics] = useState([]);
  const [sites, setSites] = useState([]);
  const [categories, setCategories] = useState({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewTopicData, setViewTopicData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  useEffect(() => {
    loadTopics();
    loadWordPressSites();
  }, []);

  useEffect(() => {
    if (sites.length > 0) {
      loadAllCategories();
    }
  }, [sites]);

  const loadTopics = async () => {
    try {
      const response = await fetch('https://contentvertex.zealcart.com/api/topics', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (data.success) {
        const sortedTopics = data.topics.sort((a, b) => 
          new Date(b.created_at) - new Date(a.created_at)
        );
        setTopics(sortedTopics);
      }
    } catch (error) {
      console.error('Failed to load topics:', error);
    }
  };

  const loadWordPressSites = async () => {
    try {
      const response = await fetch('https://contentvertex.zealcart.com/api/wordpress-sites', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (data.success) {
        setSites(data.sites);
      }
    } catch (error) {
      console.error('Failed to load sites:', error);
    }
  };

  const loadAllCategories = async () => {
    try {
      for (const site of sites) {
        const response = await fetch(`https://contentvertex.zealcart.com/api/wordpress-sites/${site.id}/categories`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
            'Content-Type': 'application/json'
          }
        });
        
        const data = await response.json();
        if (data.success) {
          setCategories(prev => ({
            ...prev,
            [site.id]: data.categories
          }));
        }
      }
    } catch (error) {
      console.error('Failed to load all categories:', error);
    }
  };

  const getCategoryName = (topic) => {
    if (!topic.category_id || !topic.wordpress_site_id) return 'Uncategorized';
    
    const siteCategories = categories[topic.wordpress_site_id];
    if (!siteCategories) return 'Uncategorized';
    
    const findCategoryPath = (categoryId, cats) => {
      const category = cats.find(cat => cat.id == categoryId);
      if (!category) return 'Uncategorized';
      
      if (category.parent === 0) {
        return category.name;
      } else {
        const parent = cats.find(cat => cat.id == category.parent);
        return parent ? `${parent.name} → ${category.name}` : category.name;
      }
    };
    
    return findCategoryPath(topic.category_id, siteCategories);
  };

  const getStatusBadge = (status, errorMessage, publishType, isScheduled, scheduledAt) => {
  const colors = {
    pending: 'bg-gray-100 text-gray-800',
    processing: 'bg-yellow-100 text-yellow-800',
    scheduled: 'bg-blue-100 text-blue-800',
    publishing: 'bg-yellow-100 text-yellow-800',
    published_draft: 'bg-purple-100 text-purple-800',
    published: 'bg-green-100 text-green-800',
    failed: 'bg-red-100 text-red-800'
  };
  
  const labels = {
    pending: 'Pending',
    processing: 'Generating...',
    scheduled: 'Scheduled',
    publishing: 'Publishing...',
    published_draft: 'Draft in WordPress',
    published: 'Published to WordPress',
    failed: 'Failed'
  };
  
  // Calculate next cron run for scheduled posts
  const getNextCronRun = (scheduledTime) => {
  if (!scheduledTime) return '';
  
  const scheduled = new Date(scheduledTime);
  
  // Round up to next 15-minute interval after scheduled time
  const minutes = scheduled.getMinutes();
  const roundedMinutes = Math.ceil(minutes / 15) * 15;
  
  const nextRun = new Date(scheduled);
  
  if (roundedMinutes >= 60) {
    // Roll over to next hour
    nextRun.setHours(nextRun.getHours() + 1);
    nextRun.setMinutes(0, 0, 0);
  } else {
    nextRun.setMinutes(roundedMinutes, 0, 0);
  }
  
  return nextRun.toLocaleDateString('en-GB') + ' ' + nextRun.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
};
  
  // Determine actual status
  let actualStatus = status;
  if (isScheduled === 1 && status === 'generated') {
    actualStatus = 'scheduled';
  } else if (status === 'published' && publishType === 'draft') {
    actualStatus = 'published_draft';
  }
  
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <span className={`status-badge ${colors[actualStatus]}`}>
        {labels[actualStatus] || status}
      </span>
      
      {actualStatus === 'scheduled' && scheduledAt && (
        <span 
          className="error-tooltip" 
          title={`Scheduled for: ${new Date(scheduledAt).toLocaleDateString()} ${new Date(scheduledAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}\nWill publish around: ${getNextCronRun(scheduledAt)}`}
          style={{
            cursor: 'help',
            color: '#3b82f6',
            fontSize: '14px',
            fontWeight: 'bold'
          }}
        >
          ℹ️
        </span>
      )}
      
      {status === 'failed' && errorMessage && (
        <span 
          className="error-tooltip" 
          title={errorMessage}
          style={{
            cursor: 'help',
            color: '#dc2626',
            fontSize: '16px'
          }}
        >
          ⚠️
        </span>
      )}
    </div>
  );
};

  const processTopic = async (topicId) => {
    try {
      setTopics(topics.map(topic => 
        topic.id === topicId ? {...topic, status: 'processing'} : topic
      ));

      const response = await fetch(`https://contentvertex.zealcart.com/api/topics/${topicId}/process`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        pollTopicStatus(topicId);
      } else {
        alert('Failed to start processing');
        loadTopics();
      }
    } catch (error) {
      alert('Failed to process topic');
      loadTopics();
    }
  };

  const pollTopicStatus = async (topicId) => {
    const maxAttempts = 30;
    let attempts = 0;

    const checkStatus = async () => {
      try {
        const response = await fetch(`https://contentvertex.zealcart.com/api/topics/${topicId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          const topic = data.topic;

          if (topic.status === 'published' || topic.status === 'failed') {
            loadTopics();
            return;
          }

          if (attempts < maxAttempts) {
            attempts++;
            setTimeout(checkStatus, 10000);
          } else {
            loadTopics();
          }
        }
      } catch (error) {
        console.error('Status polling error:', error);
        loadTopics();
      }
    };

    setTimeout(checkStatus, 10000);
  };

  const publishToWordPress = async (publishType, topic) => {
    if (!topic) return;
    
    console.log('Publishing to WordPress:', { publishType, topicId: topic.id });

    try {
      // Update UI to show publishing status
      setTopics(topics.map(t => 
        t.id === topic.id 
          ? { ...t, status: 'publishing' } 
          : t
      ));

      console.log('Making API call to:', `https://contentvertex.zealcart.com/api/topics/${topic.id}/publish`);
      
      const response = await fetch(`https://contentvertex.zealcart.com/api/topics/${topic.id}/publish`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ publish_type: publishType })
      });

      console.log('API Response status:', response.status);

      const data = await response.json();

      console.log('API Response data:', data);
      
      if (data.success) {
        alert(`Successfully ${publishType === 'draft' ? 'saved as draft' : 'published'} to WordPress!`);
        loadTopics();
      } else {
        alert('Failed to publish: ' + data.message);
        loadTopics();
      }
    } catch (error) {
      setTopics(topics.map(t => 
        t.id === topic.id 
          ? { ...t, status: 'failed', error_message: 'Network error' } 
          : t
      ));
      alert('Failed to publish to WordPress');
    }
  };

  const viewTopic = async (topicId) => {
    try {
      const response = await fetch(`https://contentvertex.zealcart.com/api/topics/${topicId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setViewTopicData(data.topic);
        setShowViewModal(true);
      } else {
        alert('Failed to load topic details');
      }
    } catch (error) {
      alert('Failed to load topic details');
    }
  };

  const formatContentForDisplay = (content) => {
    if (!content) return '';
    
    let formattedContent = content;
    
    formattedContent = formattedContent.replace(/^#{1,6}\s*.+?\n+/, '');
    formattedContent = formattedContent.replace(/^[^\n]+:\s*[^\n]+\n+/, '');
    formattedContent = formattedContent.replace(/^###?\s*(Introduction|INTRODUCTION)\s*\n/im, '');
    formattedContent = formattedContent.replace(/^##?\s*(Introduction|INTRODUCTION)\s*\n/im, '');
    formattedContent = formattedContent.replace(/^####\s*H4:\s*(.+)$/gm, '<h4>$1</h4>');
    formattedContent = formattedContent.replace(/^####\s*(.+)$/gm, '<h4>$1</h4>');
    formattedContent = formattedContent.replace(/^###?\s*(Key Takeaways|Conclusion)\s*$/gim, '<h2>$1</h2>');
    formattedContent = formattedContent.replace(/^###\s*H3:\s*(.+)$/gm, '<h3>$1</h3>');
    formattedContent = formattedContent.replace(/^###\s*(.+)$/gm, '<h3>$1</h3>');
    formattedContent = formattedContent.replace(/^##\s*H2:\s*(.+)$/gm, '<h2>$1</h2>');
    formattedContent = formattedContent.replace(/^##\s*(.+)$/gm, '<h2>$1</h2>');
    formattedContent = formattedContent.replace(/^#\s*H1:\s*(.+)$/gm, '<h1>$1</h1>');
    formattedContent = formattedContent.replace(/^#\s*(.+)$/gm, '<h1>$1</h1>');
    formattedContent = formattedContent.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    formattedContent = formattedContent.replace(/^\* (.+)$/gm, '<li>$1</li>');
    formattedContent = formattedContent.replace(/^- (.+)$/gm, '<li>$1</li>');
    formattedContent = formattedContent.replace(/(<li>.*?<\/li>\s*)+/gs, '<ul>$&</ul>');
    formattedContent = formattedContent.replace(/\n\n/g, '</p><p>');
    formattedContent = '<p>' + formattedContent + '</p>';
    formattedContent = formattedContent.replace(/<p><\/p>/g, '');
    formattedContent = formattedContent.replace(/<p>\s*<h([1-6])/g, '<h$1');
    formattedContent = formattedContent.replace(/<\/h([1-6])>\s*<\/p>/g, '</h$1>');
    formattedContent = formattedContent.replace(/<p>\s*<ul>/g, '<ul>');
    formattedContent = formattedContent.replace(/<\/ul>\s*<\/p>/g, '</ul>');
    
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = formattedContent;
    
    const excludedH2Sections = ['Key Takeaways', 'Conclusion', 'Introduction'];
    let h3Counter = 0;
    let currentH3Number = 0;
    let h4Counter = 0;
    let shouldNumberH3 = false;
    
    Array.from(tempDiv.childNodes).forEach(node => {
      if (node.nodeName === 'H2') {
        h3Counter = 0;
        currentH3Number = 0;
        h4Counter = 0;
        const h2Text = node.textContent.trim();
        shouldNumberH3 = !excludedH2Sections.some(excluded => 
          h2Text.toLowerCase().includes(excluded.toLowerCase())
        );
      }
      
      if (node.nodeName === 'H3') {
        const h3Text = node.textContent.trim();
        const isExcluded = excludedH2Sections.some(excluded => 
          h3Text.toLowerCase().includes(excluded.toLowerCase())
        );
        
        if (shouldNumberH3 && !isExcluded) {
          h3Counter++;
          currentH3Number = h3Counter;
          h4Counter = 0;
          const cleanText = node.textContent.replace(/^\d+\.\s*/, '').trim();
          node.innerHTML = `<span class="heading-number">${h3Counter}.</span> ${cleanText}`;
        }
      }
      
      if (node.nodeName === 'H4' && currentH3Number > 0) {
        h4Counter++;
        const cleanText = node.textContent.replace(/^\d+\.\d+\s*/, '').trim();
        node.innerHTML = `<span class="sub-heading-number">${currentH3Number}.${h4Counter}</span> ${cleanText}`;
      }
    });
    
    return tempDiv.innerHTML;
  };

  const removeTopic = async (topicId) => {
    if (!window.confirm('Are you sure you want to remove this topic?')) return;
    
    try {
      const response = await fetch(`https://contentvertex.zealcart.com/api/topics/${topicId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setTopics(topics.filter(topic => topic.id !== topicId));
        alert('Topic removed successfully');
      } else {
        alert('Failed to remove topic');
      }
    } catch (error) {
      alert('Failed to remove topic');
    }
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentTopics = topics.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(topics.length / itemsPerPage);

  return (
    <div className="topic-manager">
      <div className="manager-header">
        <h2>Topic Queue</h2>
        <button 
          className="btn btn-primary"
          onClick={() => setShowAddForm(true)}
        >
          + Add Topic
        </button>
      </div>

      {showAddForm && (
        <div style={{position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000}}>
          <div style={{maxWidth: '600px', width: '90%'}}>
            <QuickPost 
              user={user}
              showHeader={false}
              onClose={() => {
                setShowAddForm(false);
                loadTopics();
              }}
            />
          </div>
        </div>
      )}

      {showViewModal && viewTopicData && (
        <div className="add-form-modal">
          <div className="modal-content" style={{maxWidth: '800px', maxHeight: '90vh'}}>
            <div className="modal-header">
              <h3>Article: {viewTopicData.generated_title || viewTopicData.topic_title}</h3>
              <button onClick={() => setShowViewModal(false)}>×</button>
            </div>
            
            <div style={{padding: '24px', overflowY: 'auto'}}>
              <div className="article-meta">
                <div><strong>Site:</strong> {viewTopicData.word_press_site?.site_name || viewTopicData.wordPressSite?.site_name || viewTopicData.wordpress_site?.site_name || 'N/A'}</div>
                <div><strong>Category:</strong> {getCategoryName(viewTopicData)}</div>
                <div><strong>Status:</strong> {getStatusBadge(viewTopicData.status, viewTopicData.error_message, viewTopicData.publish_type)}</div>
                <div><strong>Type:</strong> {viewTopicData.publish_type}</div>
              </div>
              
              {viewTopicData.generated_content ? (
                <div className="article-content">
                  <h2 className="article-title">Generated Article</h2>
                  {viewTopicData.generated_excerpt && (
                    <div className="excerpt">
                      <h4>Excerpt:</h4>
                      <p>{viewTopicData.generated_excerpt}</p>
                    </div>
                  )}
                  <div 
                    className="content" 
                    dangerouslySetInnerHTML={{
                      __html: formatContentForDisplay(viewTopicData.generated_content)
                    }} 
                  />
                  {viewTopicData.generated_tags && viewTopicData.generated_tags.length > 0 && (
                    <div className="tags">
                      <h4>Tags:</h4>
                      <div className="tag-list">
                        {viewTopicData.generated_tags.map((tag, index) => (
                          <span key={index} className="tag">{tag}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="no-content">
                  <p>No generated content available. {viewTopicData.status === 'pending' ? 'Process this topic to generate content.' : ''}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="topics-table">
        <div className="table-header">
          <div>Topic Title</div>
          <div>WordPress Site</div>
          <div>Category</div>
          <div>Status</div>
          <div>Actions</div>
        </div>

        {topics.length === 0 ? (
          <div className="no-topics">
            <p>No topics in queue. Add your first topic to get started!</p>
          </div>
        ) : (
          currentTopics.map(topic => (
            <div key={topic.id} className="table-row">
              <div className="topic-title">{topic.topic_title}</div>
              <div>{topic.word_press_site?.site_name || topic.wordpress_site?.site_name || 'N/A'}</div>
              <div>{getCategoryName(topic)}</div>
              <div>{getStatusBadge(topic.status, topic.error_message, topic.publish_type, topic.is_scheduled, topic.scheduled_at)}</div>
              <div className="actions">
            {topic.status === 'pending' ? (
              <button 
                className="btn-sm btn-primary"
                onClick={() => processTopic(topic.id)}
              >
                Process
              </button>
            ) : null}

            {(topic.is_scheduled === 1 && topic.status === 'generated') ? (
              <button 
                className="btn-sm"
                onClick={() => publishToWordPress(topic.publish_type, topic)}
                style={{ background: '#3b82f6', color: 'white' }}
              >
                Publish Now
              </button>
            ) : null}

            {(topic.status === 'published' && topic.publish_type === 'draft') ? (
              <button 
                className="btn-sm btn-publish"
                onClick={() => publishToWordPress('publish', topic)}
                style={{
                  background: '#10b981',
                  color: 'white'
                }}
              >
                Publish Live
              </button>
            ) : null}

            {(topic.status === 'published' && topic.wordpress_url && topic.publish_type === 'publish') ? (
              <button 
                className="btn-sm btn-info"
                onClick={() => window.open(topic.wordpress_url, '_blank')}
              >
                View on WordPress
              </button>
            ) : null}

            {topic.status === 'failed' ? (
              <>
                <button 
                  className="btn-sm"
                  onClick={() => processTopic(topic.id)}
                  style={{ background: '#dc2626', color: 'white' }}
                >
                  Retry Generation
                </button>
                <button 
                  className="btn-sm btn-danger"
                  onClick={() => removeTopic(topic.id)}
                  style={{ marginLeft: '4px' }}
                >
                  Delete
                </button>
              </>
            ) : null}
            <button 
              className="btn-sm btn-secondary"
              onClick={() => viewTopic(topic.id)}
            >
              View
            </button>
            </div>
            </div>
          ))
        )}
      </div>

      {topics.length > itemsPerPage && (
        <div className="pagination">
          <button 
            className="pagination-btn"
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
          >
            <span>←</span> Previous
          </button>
          
          <div className="page-numbers">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => {
              const showPage = 
                pageNum <= 3 || 
                pageNum > totalPages - 3 || 
                (pageNum >= currentPage - 1 && pageNum <= currentPage + 1);
              
              const showEllipsisBefore = pageNum === 4 && currentPage > 5;
              const showEllipsisAfter = pageNum === totalPages - 3 && currentPage < totalPages - 4;
              
              if (showEllipsisBefore) {
                return <span key={`ellipsis-${pageNum}`} className="page-ellipsis">...</span>;
              }
              
              if (showEllipsisAfter) {
                return <span key={`ellipsis-${pageNum}`} className="page-ellipsis">...</span>;
              }
              
              if (!showPage) return null;
              
              return (
                <button
                  key={pageNum}
                  className={`page-number ${currentPage === pageNum ? 'active' : ''}`}
                  onClick={() => setCurrentPage(pageNum)}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>
          
          <button 
            className="pagination-btn"
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
          >
            Next <span>→</span>
          </button>
        </div>
      )}
    </div>
  );
}

export default TopicManager;